/**
 * Flag debug bus — the data behind the engineer-facing flag overlay.
 *
 * Every browser-initiated flag interaction publishes here: the `__BM_DATA__`
 * snapshot the page loaded with, on-demand resolves, exposures (and whether they
 * were actually recorded), and goals. An engineer enables the overlay in any
 * environment, including production, and watches the pipeline behave on the real
 * app rather than inferring it from PostHog after the fact.
 *
 * DESIGN CONSTRAINTS, because this ships to production:
 *
 * - **Tiny and always-on.** This module is the only always-loaded piece; the
 *   overlay UI is imported lazily and only when enabled, so a normal visitor
 *   downloads nothing beyond the few hundred bytes below.
 * - **Records even while disabled**, into a bounded ring buffer. Enabling the
 *   overlay after something odd happened still shows the history that explains
 *   it, which is the difference between a usable tool and one you have to
 *   remember to turn on first.
 * - **Never carries identity.** The gate is `localStorage`, which any visitor can
 *   set, so it is a visibility switch and NOT access control. Events therefore
 *   carry flag keys, values, and the *shape* of the distinct id — never the id
 *   itself, and never an email.
 * - **Never throws into a caller.** A publish failure must not break a flag read.
 */

/**
 * Which KEY SHAPE a flag read or write bucketed on — never the value (see the
 * "never carries identity" note above).
 *
 * `community` means "not an admin": tribe-neo keys an admin on their identity and
 * everyone else on the community network id, which is exactly why an exposure was
 * declined for them.
 *
 * `member-id` is a real case, not a theoretical one: `buildFlagContext` keys an
 * admin on `normalizeIdentityEmail(email) ?? memberId`, so an admin with no
 * email buckets on their member id. That is precisely the situation someone
 * opens this overlay for ("the same admin got variant A in nexus-signup and
 * variant B here"), so collapsing it into `email` would rule out the real cause.
 */
export type FlagIdentityShape = 'email' | 'member-id' | 'community' | 'unknown'

export type FlagDebugEventInput =
  | {
      kind: 'snapshot'
      at: number
      /** Flags the page was server-rendered with (the `client`-tagged set). */
      flags: Record<string, boolean>
    }
  | {
      kind: 'resolve'
      at: number
      key: string
      value: boolean | string | null
      /** Milliseconds the `/api/flags` round-trip took. */
      ms: number
    }
  | {
      kind: 'expose'
      at: number
      key: string
      /**
       * What the server actually did, derived from the endpoint's status code.
       * `skipped-not-admin` is the interesting one: experiments are an
       * admin-surface concern, so `/api/flags/exposure` returns 204 and records
       * nothing for anyone else rather than landing it on the community person.
       *
       * `rejected-cross-origin` is a 403 from the same-origin guard, broken out of
       * `error` because it has a cause a reader can act on and `error` does not.
       * It fires whenever the browser's `Origin` disagrees with the host the
       * request was addressed to — in practice on a **review app**, where
       * `FORCED_NETWORK_DOMAIN` makes `req.hostname` report a vanity domain rather
       * than the review host. Told apart from a transport failure, that is a
       * two-second diagnosis; folded into `error` it is a code read.
       */
      outcome:
        | 'exposed'
        | 'skipped-not-admin'
        | 'not-experiment'
        | 'rejected-cross-origin'
        | 'error'
      identity: FlagIdentityShape
    }
  | { kind: 'goal'; at: number; event: string; forwarded: boolean }

/**
 * A recorded event. `seq` is assigned on publish and is monotonic for the life of
 * the page: two events can share a millisecond, so `at` alone is not a stable
 * React key, and ordering must not depend on array position once the log is
 * rendered newest-first.
 */
export type FlagDebugEvent = FlagDebugEventInput & { seq: number }

/** Bounded so a long session cannot grow memory without limit. */
const MAX_EVENTS = 200

const events: FlagDebugEvent[] = []
let nextSeq = 0
const listeners = new Set<(events: readonly FlagDebugEvent[]) => void>()

export const STORAGE_KEY = 'bm_flag_debug'

export const getFlagDebugEvents = (): readonly FlagDebugEvent[] => events

export const publishFlagDebugEvent = (event: FlagDebugEventInput): void => {
  try {
    nextSeq += 1
    events.push({ ...event, seq: nextSeq })
    if (events.length > MAX_EVENTS) {
      events.splice(0, events.length - MAX_EVENTS)
    }
    listeners.forEach(listener => listener(events))
  } catch {
    // A debug surface must never break the flag read that fed it.
  }
}

export const subscribeToFlagDebug = (
  listener: (events: readonly FlagDebugEvent[]) => void,
): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const isFlagDebugEnabled = (): boolean => {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) === '1'
  } catch {
    // Safari private mode and similar throw on access rather than returning null.
    return false
  }
}

export const setFlagDebugEnabled = (enabled: boolean): void => {
  try {
    // Nothing to do when the stored value already matches — and this guard is
    // load-bearing, not tidiness. Without it, any caller that re-asserts the
    // current state on every page load writes and then reloads, which runs again
    // and reloads again. Communities inject arbitrary third-party JS into this
    // same `window` via `network.customCodes`, so that loop is reachable from
    // outside our own code and would leave every visitor to that community stuck
    // with no product-visible off switch.
    if (isFlagDebugEnabled() === enabled) {
      return
    }

    if (enabled) {
      globalThis.localStorage?.setItem(STORAGE_KEY, '1')
    } else {
      globalThis.localStorage?.removeItem(STORAGE_KEY)
    }
    // The overlay mounts at the root; a reload is the simplest way to pick up a
    // change without shipping a second always-on listener just for this.
    globalThis.location?.reload()
  } catch {
    // Ignore — the caller is a console helper, not application code.
  }
}
